import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useModalState } from '@/js/libs/hooks/useModalState';
import { useNavigate } from '@/js/libs/router';
import { Button, DropdownList, Icons } from '@/js/libs/ui/src/V2';
import { PrivateContent, Roles, or, not, useIdentityUser, useRolesMatch } from '@/js/libs/use-auth';

import { ROUTER } from 'src/AppRouter';
import useAppLogout from 'src/hooks/useAppLogout';
import { GrantAccessModal } from 'src/modules/CompaniesSwitch/containers/GrantAccessModal';
import { useCompanySwitchContext } from 'src/modules/CompaniesSwitch/context';

export const AccountButton = memo(function AccountButton() {
  const [t] = useTranslation(['common', 'components']);
  const navigate = useNavigate();
  const logout = useAppLogout();
  const company = useIdentityUser()?.company;
  const grantAccessModalState = useModalState();
  const isAdmin = useRolesMatch({ roles: or(Roles.ADMIN, Roles.COMPANY_ADMIN) });
  const switchedCompany = useCompanySwitchContext((state) => state.company);
  const isTemporaryAccessRequired = Boolean(company?.temporaryAccessRequired);

  const canViewAdminLogSessions = isAdmin || isTemporaryAccessRequired;

  const handleNavigateToSettings = useCallbackRef(() => {
    navigate(ROUTER['/account/edit-settings'].path.pattern);
  });

  const handleNavigateToCompany = useCallbackRef(() => {
    navigate(ROUTER['/account/company'].path.pattern);
  });

  const handleGrantAdminAccess = useCallbackRef(() => {
    grantAccessModalState.open();
  });

  const handleNavigateToAdminAccessHistory = useCallbackRef(() => {
    navigate(ROUTER['/account/admin-access-history'].path.pattern);
  });

  return (
    <>
      {grantAccessModalState.isMounted && company && <GrantAccessModal state={grantAccessModalState} companyId={company.id} />}
      <DropdownList
        minWidth={false}
        dataTestId='account-button'
        renderButton={() => (
          <Button iconOnly ghost title={t('common:PROFILE')}>
            <Icons.Person size={20} />
          </Button>
        )}>
        {isTemporaryAccessRequired && !switchedCompany && (
          <PrivateContent roles={Roles.CUSTOMER}>
            <DropdownList.DropdownItem icon={<Icons.AdConsole />} onClick={handleGrantAdminAccess} dataTestId='grant-admin-access-button'>
              {t('components:GRANT_ADMIN_ACCESS')}
            </DropdownList.DropdownItem>
          </PrivateContent>
        )}

        {canViewAdminLogSessions && !switchedCompany && (
          <DropdownList.DropdownItem
            icon={<Icons.List />}
            onClick={handleNavigateToAdminAccessHistory}
            dataTestId='admin-log-sessions-button'>
            {t('components:ADMIN_LOG_SESSIONS')}
          </DropdownList.DropdownItem>
        )}

        <PrivateContent roles={not(Roles.SSO)}>
          <DropdownList.DropdownItem icon={<Icons.Person />} onClick={handleNavigateToSettings} dataTestId='manage-account-button'>
            {t('components:MANAGE_ACCOUNT')}
          </DropdownList.DropdownItem>
        </PrivateContent>

        <PrivateContent roles={or(Roles.ADMIN, Roles.COMPANY_ADMIN)}>
          <DropdownList.DropdownItem icon={<Icons.Company />} onClick={handleNavigateToCompany} dataTestId='manage-company-button'>
            {t('components:COMPANY_EDIT')}
          </DropdownList.DropdownItem>
        </PrivateContent>

        <DropdownList.DropdownItem icon={<Icons.Logout />} onClick={logout} dataTestId='user-logout-button'>
          {t('components:LOGOUT')}
        </DropdownList.DropdownItem>
      </DropdownList>
    </>
  );
});
